import AsyncData from 'app-lib/mobx/AsyncData'
import { FulcrumProvider } from 'bzx-common/../fulcrum/src/services/FulcrumProvider'
import { chainConfigs } from 'bzx-common/src/config/appConfig'
import IBorrowedFundsState from 'bzx-common/src/domain/IBorrowedFundsState'
import ManageCollateralRequest from 'bzx-common/src/domain/ManageCollateralRequest'
import {
  IClosePositionRequest,
  IOpenPositionProps,
  IPendingPosition,
  ITradeRequest,
  PositionType,
} from 'bzx-common/src/domain/trade.types'
import assetUtils, { Asset } from 'bzx-common/src/lib/assetUtils'
import errorUtils from 'bzx-common/src/lib/errorUtils'
import loanUtils from 'bzx-common/src/lib/loanUtils'
import tradeUtils from 'bzx-common/src/lib/tradeUtils'
import * as mobx from 'mobx'
import RootStore from '../../stores/RootStore'
import HistoryToken from './HistoryToken'
import NewPositionSetup from './NewPositionSetup'
import OpenPosition from './OpenPosition'

interface ITradeStoreProps {
  activePositionType: PositionType
  allOpenPositions: AsyncData<OpenPosition[]>
  errorHistory: Error | null
  errorPosition: Error | null
  errorTask: Error | null
  fulcrumProvider: FulcrumProvider
  historyPending: boolean
  historyTokens: HistoryToken[]
  historyFromOldGraph: boolean
  isHistoryLoaded: boolean
  isOpen: boolean
  isTradeCompleted: boolean
  isTxCompleted: boolean
  loanId: string | null
  loansChecked: boolean
  newPositionSetup: NewPositionSetup
  openPositionsPending: boolean
  pending: boolean
  pendingPositions: IPendingPosition[]
  tradeRequest: ITradeRequest | null
  manageCollateralRequest: ManageCollateralRequest | null
  tradeHistoryFetchFailed: boolean
  positionToClose?: OpenPosition
}

export default class TradeStore implements ITradeStoreProps {
  activePositionType = PositionType.LONG

  /**
   * Open positions for any pair that the user has currently open.
   */
  allOpenPositions: AsyncData<OpenPosition[]> = new AsyncData<OpenPosition[]>(
    [],
    async () => {
      const loans = await tradeUtils.getUserMarginTradeLoans(this.fulcrumProvider)
      const positions = await this.getPositionsFromLoans(loans)
      return positions.map((position) => new OpenPosition(this.rootStore, position))
    },
    {
      onError(err) {
        errorUtils.decorateError(err, { title: 'Could not list your positions' })
      },
    }
  )

  autoSyncData = false
  defaultQuantityVisibleRow = 8
  errorHistory = null
  errorPosition = null
  errorTask = null
  fulcrumProvider: FulcrumProvider
  historyFromOldGraph = false
  historyPending = false
  historyTokens: HistoryToken[] = []
  isHistoryLoaded = false
  isOpen = false
  isTradeCompleted = false
  isTradeModalOpen = false
  isTxCompleted = false
  loanId = null
  loansChecked = false
  loansPending = true
  manageCollateralRequest: ManageCollateralRequest | null = null
  openPositionsPending = false
  pending = false
  /** List positions that are in the process of being opened */
  pendingPositions: IPendingPosition[] = []
  rootStore: RootStore
  tradeHistoryFetchFailed = false
  tradeRequest: ITradeRequest | null = null
  newPositionSetup: NewPositionSetup
  positionToClose?: OpenPosition

  /**
   * These are details specifically for the Tradingview chart.
   * - it replaces USDC quote by USDT to work with Binance since they remove USDC
   * - it returns datafeedUrl, actual market pair, and pair modified for the feed
   *
   * Note: It would be better not to have this inside tradeStore for separation of concerns
   */
  get tvChartDetails() {
    const { chainId } = this.rootStore.web3Connection
    const selectedMarket = this.newPositionSetup.pairConfig?.selectedMarket
    if (!selectedMarket) {
      return
    }
    const datafeedUrl = chainId ? chainConfigs[chainId].chartUDF.url : ''
    let marketSymbol = ''
    const base = selectedMarket?.baseToken || ''
    const quote = selectedMarket?.quoteToken || ''

    if (selectedMarket && chainId) {
      const { chartUDF } = chainConfigs[chainId]
      let tvBaseToken: string = assetUtils.checkWethToken(chartUDF.id, selectedMarket.baseToken)
      if (tvBaseToken === Asset.BTCB) {
        // use BTC instead BTCB
        tvBaseToken = 'BTC'
      }
      let tvQuoteToken = assetUtils.checkWethToken(chartUDF.id, selectedMarket.quoteToken)
      if (tvQuoteToken === Asset.USDC) {
        // At the moment, we will use USDT as quote for USDC pairs until we can have all charts
        // USDC/USDT pair should still be visible
        tvQuoteToken = Asset.USDT
      }
      marketSymbol = `${tvBaseToken}${chartUDF.symbolSeparator}${tvQuoteToken}`
    }

    return { datafeedUrl, marketSymbol, base, quote }
  }

  get loan() {
    return this.allOpenPositions.value.find((item) => this.loanId === item.loan.loanId)?.loan
  }

  get userHasNoPositions() {
    const { loaded, pending, value } = this.allOpenPositions
    return !pending && loaded && value.length === 0
  }

  get error() {
    return (
      this.errorPosition ||
      this.allOpenPositions.error ||
      this.errorHistory ||
      this.errorTask ||
      this.newPositionSetup.balancesError
    )
  }

  get assetsShown() {
    const { chainId } = this.rootStore.web3Connection

    return chainId ? chainConfigs[chainId].borrowing.assets : null
  }

  /** Only Polygon and BSC have this old history support */
  get oldHistoryAvailable() {
    const { chainId, isConnected } = this.rootStore.web3Connection
    return isConnected && [56, 137].includes(chainId as number)
  }

  /**
   * This gets the positions for the currently selected market
   */
  async getPositionsFromLoans(loans: IBorrowedFundsState[]): Promise<IOpenPositionProps[]> {
    const openPositions: IOpenPositionProps[] = []
    for (const loan of loans) {
      try {
        if (!loan.loanData) {
          throw new Error('Could not get loanData for loan ' + loan.loanId)
        }
        const positionDetails = await tradeUtils.getLoanDetails(this.fulcrumProvider, loan)
        openPositions.push(positionDetails as IOpenPositionProps)
      } catch (err) {
        errorUtils.decorateError(err, { title: 'Could not list some positions' })
        this.set('errorPosition', err)
        console.error(err)
      }
    }
    return openPositions
  }

  manageCollateral(request: ManageCollateralRequest) {
    try {
      this.setPositionPendingTx(request.loanId)
      this.collateral(request)
      this.onClose()
    } catch (err) {
      errorUtils.decorateError(err, { title: 'Failed to submit manage collateral' })
      this.set('errorPosition', err)
      console.error(err)
    }
  }

  onTransactionsCompleted = async () => {
    const isTxCompleted = !this.isTxCompleted
    this.assign({ isTxCompleted })
    this.clearData()
  }

  changeGridPositionType = async (activePositionType: PositionType) => {
    this.assign({ activePositionType })
  }

  setPositionPendingTx(loanId: string | undefined, pendingTx = true) {
    const openPosition = this.allOpenPositions.value.find((pos) => pos.loan.loanId === loanId)
    if (openPosition) {
      openPosition.set('pendingTx', pendingTx)
    }
  }

  updatePosition(updatedPosition: OpenPosition) {
    const newPositions = this.allOpenPositions.value.map((position) =>
      updatedPosition.loan.loanId === position.loan.loanId ? updatedPosition : position
    )
    this.allOpenPositions.set('value', newPositions)
    this.clearHistory()
  }

  removePosition(loanId: string) {
    const allOpenPositions = this.allOpenPositions.value.filter((pos) => pos.loan.loanId !== loanId)
    this.allOpenPositions.set('value', allOpenPositions)
    this.clearHistory()
  }

  requestClosePosition(tradeRequest: ITradeRequest, positionToClose: OpenPosition) {
    this.assign({ tradeRequest, isOpen: true, loanId: tradeRequest.loanId, positionToClose })
  }

  requestManageCollateral(manageCollateralRequest: ManageCollateralRequest) {
    this.assign({ manageCollateralRequest, isOpen: true, loanId: manageCollateralRequest.loanId })
  }

  /** This adds a placeholder for a position that is being opened  */
  getPendingPositionFromRequest(request: ITradeRequest): IPendingPosition {
    return {
      baseToken: request.baseToken,
      quoteToken: request.quoteToken,
      positionType: request.positionType,
      leverage: request.leverage,
      positionValue: request.positionValue || NaN,
      time: Date.now(),
    }
  }

  async closePosition(request: IClosePositionRequest, position: OpenPosition) {
    const { fulcrumProvider, web3Connection, transactionStore } = this.rootStore
    const { loanService } = this.rootStore.services
    const { chainId } = web3Connection
    const { loanId } = request
    if (!chainId) {
      return
    }
    position.assign({ pendingTx: true })
    this.onClose()
    let tx
    try {
      const txSteps = await loanService.tradeClose(chainId, request, position.loan)
      const type =
        request.positionType === PositionType.LONG ? 'trade-close-long' : 'trade-close-short'
      tx = await transactionStore.run(type, txSteps)
      if (tx.status === 'success') {
        const loanData = await loanUtils.getLoanById(fulcrumProvider, loanId)
        if (loanData) {
          const positions = await this.getPositionsFromLoans([loanData])
          return this.updatePosition(new OpenPosition(this.rootStore, positions[0]))
        }
        return this.removePosition(loanId)
      } else {
        position.assign({ pendingTx: false })
      }
      return tx
    } catch (err) {
      console.error(err)
      this.set('errorPosition', err)
    }
  }

  async trade(request: ITradeRequest) {
    const { web3Connection, transactionStore } = this.rootStore
    const { loanService } = this.rootStore.services
    const { chainId } = web3Connection
    if (!chainId) {
      return
    }
    this.onClose()
    const pendingPosition = this.getPendingPositionFromRequest(request)

    let tx
    try {
      this.pendingPositions = this.pendingPositions.concat(pendingPosition)
      const txSteps = await loanService.tradeOpen(chainId, request)
      const type =
        request.positionType === PositionType.LONG ? 'trade-open-long' : 'trade-open-short'
      tx = await transactionStore.run(type, txSteps)
      if (tx.status === 'success') {
        await this.allOpenPositions.fetch()
        // TODO: in VM, we should refresh tradeHistory if we are on that section after success
      }
      this.pendingPositions = this.pendingPositions.filter((pos) => pos !== pendingPosition)
      return tx
    } catch (err) {
      console.error(err)
      this.pendingPositions = this.pendingPositions.filter((pos) => pos !== pendingPosition)
      this.set('errorPosition', err)
    }
  }

  async collateral(request: ManageCollateralRequest) {
    const { fulcrumProvider, web3Connection, transactionStore } = this.rootStore
    const { loanService } = this.rootStore.services
    const { chainId } = web3Connection
    const { loanId } = request
    if (!chainId) {
      return
    }
    let tx
    try {
      const txSteps = await loanService.collateral(chainId, request)
      tx = await transactionStore.run('trade-collateral', txSteps)
      if (tx.status === 'success') {
        const loanData = await loanUtils.getLoanById(fulcrumProvider, loanId)
        if (loanData) {
          const positions = await this.getPositionsFromLoans([loanData])
          return this.updatePosition(new OpenPosition(this.rootStore, positions[0]))
        }
      } else {
        this.setPositionPendingTx(loanId, false)
      }
      return tx
    } catch (err) {
      console.error(err)
    }
  }

  /**
   * Helper to set values through mobx actions.
   */
  public set(prop: keyof ITradeStoreProps, value: any) {
    ;(this[prop] as any) = value
  }

  /**
   * Helper to assign multiple props values through a mobx action.
   */
  assign(props: Partial<ITradeStoreProps>) {
    Object.assign(this, props)
  }

  clearData() {
    this.onClose()
    this.allOpenPositions.reset()
    this.clearHistory()
  }

  clearHistory() {
    this.isHistoryLoaded = false
    this.historyTokens = []
  }

  // TODO: there shouldnt be UI component state mixed in stores
  onClose() {
    this.loanId = null
    this.tradeRequest = null
    this.isOpen = false
  }

  /**
   * Reset error state to null for any model that might have an error.
   * tradeStore.error computed value will be null as a result.
   */

  clearError() {
    this.errorHistory = null
    this.errorPosition = null
    this.allOpenPositions.error = null
    this.errorTask = null
    this.newPositionSetup.clearError()
  }

  /**
   *  Sync all related data for history tab.
   */
  async getTradingHistory(opts?: { oldGraph: boolean; allNetworks: boolean }) {
    this.assign({ historyPending: true, historyFromOldGraph: opts?.oldGraph || false })
    try {
      const historyEvents = await tradeUtils.getHistoryEvents(this.fulcrumProvider, opts)
      if (!historyEvents) {
        return
      }
      const historyTokenGrid = await tradeUtils.getHistoryRowsData(
        this.fulcrumProvider,
        historyEvents
      )

      const historyTokens = historyTokenGrid.map((event) => {
        return new HistoryToken(this.rootStore, event.eventsGroup)
      })

      this.assign({ historyTokens })
    } catch (err) {
      errorUtils.decorateError(err, { title: 'Could not fetch history data' })
      this.set('errorHistory', err)
    } finally {
      this.assign({ isHistoryLoaded: true, historyPending: false })
    }
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    this.fulcrumProvider = rootStore.fulcrumProvider
    this.newPositionSetup = new NewPositionSetup(rootStore)
    mobx.makeAutoObservable(this, { rootStore: false }, { autoBind: true, deep: false })
  }
}
